import { useMemo } from "react";
import { BookingEventWithPrice } from "../../../models/booking/BookingEventWithPrice";
import { ChargeType, InvoiceModel } from "../../../data/entities";
import { BookingWithDetails } from "../../../models/booking/BookingWithDetails";


interface UseInvoiceCalculationsProps {
  booking: BookingWithDetails | undefined;
  invoice: InvoiceModel | undefined;
}

interface InvoiceCalculations {
  chargeEvents: BookingEventWithPrice[];
  holdEvents: BookingEventWithPrice[];
  noChargeEvents: BookingEventWithPrice[];
  totalChargesSubtotal: number;
  totalHoldsSubtotal: number;
  chargesTax: number;
  holdsTax: number;
  totalCharges: number;
  totalHolds: number;
  hasCharges: boolean;
  hasHolds: boolean;
}

export const useInvoiceCalculations = ({ 
  booking, 
  invoice 
}: UseInvoiceCalculationsProps): InvoiceCalculations => {

  const computeChargeAmount = (event: BookingEventWithPrice): number => {
    return event.priceDetails?.finalChargeAmount ?? 0;
  };

  const calculations = useMemo(() => {
    if (!booking || !invoice) {
      return {
        chargeEvents: [],
        holdEvents: [],
        noChargeEvents: [],
        totalChargesSubtotal: 0,
        totalHoldsSubtotal: 0,
        chargesTax: 0,
        holdsTax: 0,
        totalCharges: 0,
        totalHolds: 0,
        hasCharges: false,
        hasHolds: false,
      };
    }

    // Separate events by charge type
    const chargeEvents = booking.bookingEvents?.filter(event => {
      const price = event.priceDetails;
      return price?.appliedChargeType === ChargeType.Charge && computeChargeAmount(event) > 0;
    }) || [];

    const holdEvents = booking.bookingEvents?.filter(event => {
      const price = event.priceDetails;
      return price?.appliedChargeType === ChargeType.Hold && computeChargeAmount(event) > 0;
    }) || [];

    const noChargeEvents = booking.bookingEvents?.filter(event => {
      return computeChargeAmount(event) === 0;
    }) || [];

    // Calculate totals and taxes
    const totalChargesSubtotal = chargeEvents.reduce(
      (sum, event) => sum + computeChargeAmount(event), 
      0
    );
    
    const totalHoldsSubtotal = holdEvents.reduce(
      (sum, event) => sum + computeChargeAmount(event), 
      0
    );

    // Calculate tax for each category
    const calculateTaxForCategory = (chargeType: ChargeType | null): number => {
      const categoryItems = invoice.items?.filter(item => {
        if (chargeType === null) {
          return false;
        }
        return item.chargeType === chargeType;
      }) || [];

      return categoryItems.reduce((taxSum, item) => {
        if (item.taxes && item.taxes.length > 0) {
          const itemTaxTotal = item.taxes.reduce((sum, itemTax) => {
            return sum + (itemTax.value || 0);
          }, 0);
          return taxSum + itemTaxTotal;
        }
        return taxSum;
      }, 0);
    };

    const chargesTax = calculateTaxForCategory(ChargeType.Charge);
    const holdsTax = calculateTaxForCategory(ChargeType.Hold);

    const totalCharges = totalChargesSubtotal + chargesTax;
    const totalHolds = totalHoldsSubtotal + holdsTax;

    const hasCharges = totalCharges > 0;
    const hasHolds = totalHolds > 0;

    return {
      chargeEvents,
      holdEvents,
      noChargeEvents,
      totalChargesSubtotal,
      totalHoldsSubtotal,
      chargesTax,
      holdsTax,
      totalCharges,
      totalHolds,
      hasCharges,
      hasHolds,
    };
  }, [booking, invoice]);

  return {
    ...calculations,
  };
};